/**
 * The Oud Collection experience on the Oud PDPs (snippets/dossier-oud-collection.liquid):
 * interactive hero, bundle, one product + moodboard section per scent and the "Oud lips" ending,
 * all under one light cursor whose colour follows the scent on screen. Same engines as the US site
 * (app/components/oud/*), wired with plain DOM instead of React.
 *
 * Needs oud-gl.js, gsap.min.js and ScrollTrigger.min.js before it; site-sound.js (site-wide) for the
 * videos' sound and the header measurement.
 */
(function () {
  if (customElements.get('oud-collection')) return;
  const {
    HASH_GLSL,
    TRAIL_GLOW_GLSL,
    AMBIENT_GRAIN_GLSL,
    MAX_DPR,
    LIGHT_SIZE,
    compileProgram,
    createTexture,
    isWebGL2Supported,
    LightTrail,
    AmbientLight,
  } = window.OudGL;

  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  /** Bottom edge of the site's sticky header (see site-sound.js). */
  const headerBottom = () => window.DossierSound?.fixedHeaderBottom?.() ?? 0;
  const rand = (min, max) => min + Math.random() * (max - min);

  // ===============================================================================================
  // Hero engine: the hero media (poster, then the video's frames) masked behind a stack of wobbly
  // "tree rings" that morph and grow per tap until it covers the section, the pointer's light trail
  // (gold over the dark, ember red over the media) and film grain, all on one canvas. The ring
  // outline is a polar radius function sampled into a 128x1 texture so the shader and the JS hit
  // test share the exact same shape.

  const SHAPE_SAMPLES = 128;

  // Innermost ring reveals the media fully, outer rings show it fainter and softer.
  const RING_STEP = [1, 1.12, 1.25, 1.4, 1.57];
  const RING_ALPHA = [1, 0.6, 0.35, 0.18, 0.08];

  // ring outline harmonics: [harmonic, min amplitude, max amplitude]
  const SHAPE_HARMONICS = [
    [2, 0.05, 0.1],
    [3, 0.04, 0.08],
    [4, 0.02, 0.05],
    [5, 0.01, 0.03],
    [6, 0.005, 0.02],
    [7, 0, 0.015],
  ];
  // offset + finer wobble that grow with each outer ring, so the rings are not perfectly concentric
  const DETAIL_HARMONICS = [
    [1, 0.02, 0.04],
    [5, 0.006, 0.014],
    [7, 0.004, 0.01],
    [9, 0.002, 0.008],
  ];
  const ELLIPSE_MINOR = 0.47; // blob is ~2.1:1, lying down on desktop and standing up on mobile

  // Idle flex: standing waves that swell and settle (~3–10s cycles) with a slight drift around the
  // outline. [harmonic, weight, swell speed (rad/s), drift (rad/s)]. Weights sum to ~1.
  // the whole stack, inner ring included (so the hit test follows it)
  const INNER_FLEX = [
    [2, 0.45, 0.75, 0.06],
    [3, 0.3, 1.1, -0.09],
    [5, 0.25, 1.55, 0.14],
  ];
  // extra flex that fades in toward the middle rings...
  const MID_FLEX = [
    [2, 0.35, 0.9, -0.07],
    [3, 0.3, 1.25, 0.11],
    [4, 0.35, 1.7, -0.16],
  ];
  // ...and the outermost ones, which sway slightly off-centre too
  const OUTER_FLEX = [
    [1, 0.25, 0.6, 0.05],
    [2, 0.3, 1.0, -0.08],
    [4, 0.25, 1.45, 0.15],
    [6, 0.2, 2.0, -0.22],
  ];
  // Relative amplitude, capped in CSS pixels so the motion stays subtle as the opening grows.
  const INNER_FLEX_AMOUNT = 0.04;
  const INNER_FLEX_MAX_PX = 10;
  const OUTER_FLEX_AMOUNT = 0.07;
  const OUTER_FLEX_MAX_PX = 22;
  const RING_BREATHE = 0.25; // rings drift slightly apart and back together (fraction of the outer flex)
  const RING_BREATHE_SPEED = 0.7;

  // Every tap moves the reveal target one step and the opening eases after it. HALF_TAPS taps
  // uncover about half of the media, REVEAL_TAPS all of it.
  const HALF_TAPS = 5;
  const REVEAL_TAPS = 15;
  const TAP_EASE_TAU = 0.3;
  const AUTO_REVEAL_SPEED = 0.9; // progress per second once the reveal is forced (scroll / a11y)
  const OVERSHOOT = 1.2; // tapped open: the rings keep expanding past the edges while they fade
  const SCROLL_EASE_TAU = 0.12; // smooths scroll-driven progress (wheel steps, touch flicks)
  const HERO_LIGHT_SIZE = 0.45; // the light behind the hero's small ring cursor
  const HERO_AMBIENT_STRENGTH = 0.15; // the drifting waves in the dark around the rings
  const REVEALED_AT = 0.995;
  const UNREVEALED_BELOW = 0.985; // hysteresis so the revealed state doesn't flicker at the edge
  const RINGS_FADE_S = 0.9;
  const RINGS_RETURN_S = 0.25; // scrolling back: the rings come back right away
  // Full size = the inner ring's current outline just clears every edge (plus this margin).
  const FIT_MARGIN = 1.03;
  const MORPH_TAU = 0.28;
  const PULSE_KICK = 3.5;
  const PULSE_STIFFNESS = 180;
  const PULSE_DAMPING = 14;
  const PULSE_SCALE = 0.07;

  const HERO_SHADER = `#version 300 es
precision highp float;
in vec2 vUv;
out vec4 outColor;
uniform sampler2D uImage;
uniform sampler2D uShape; // r: inner radius, g: per-ring detail, b/a: mid/outer ring flex
uniform sampler2D uTrail;
uniform sampler2D uAmbient;
uniform vec2 uRes;
uniform float uImageAspect;
uniform float uScale;
uniform float uRings;
uniform float uTime;
uniform float uLight;
uniform float uGrainSize;
uniform float uRingStep[${RING_STEP.length}];
uniform float uRingAlpha[${RING_STEP.length}];

const float TAU = 6.28318530718;
const vec3 BG = vec3(0.078, 0.075, 0.075);
const vec3 GOLD = vec3(0.98, 0.64, 0.2);
const vec3 EMBER = vec3(0.55, 0.07, 0.02);
const vec3 RING_LINE = vec3(1.0, 0.32, 0.04);

${HASH_GLSL}
${TRAIL_GLOW_GLSL}
${AMBIENT_GRAIN_GLSL}

vec3 cover(vec2 uv) {
  float aspect = uRes.x / uRes.y;
  vec2 s = aspect > uImageAspect ? vec2(1.0, uImageAspect / aspect) : vec2(aspect / uImageAspect, 1.0);
  return texture(uImage, (uv - 0.5) * s + 0.5).rgb;
}

void main() {
  float aspect = uRes.x / uRes.y;
  vec2 p = (vUv - 0.5) * vec2(aspect, 1.0);
  float r = length(p);
  vec4 shape = texture(uShape, vec2(atan(p.y, p.x) / TAU + ${(0.5 / SHAPE_SAMPLES).toFixed(6)}, 0.5));
  float px = 1.0 / uRes.y;

  float alpha = 0.0;
  float lines = 0.0;
  float outerDistance = 0.0;
  for (int k = 0; k < ${RING_STEP.length}; k++) {
    float fk = float(k);
    float t = fk / ${(RING_STEP.length - 1).toFixed(1)};
    float radius = uScale * uRingStep[k] * shape.x * (1.0 + fk * shape.y + t * mix(shape.z, shape.w, t));
    float sd = r - radius;
    float feather = px * 1.5 + radius * (0.004 + 0.012 * fk);
    alpha = max(alpha, (1.0 - smoothstep(-feather, feather, sd)) * uRingAlpha[k]);
    lines += (1.0 - smoothstep(0.0, px * 1.2, abs(sd))) * uRingAlpha[k];
    outerDistance = sd;
  }

  float reveal = mix(1.0, alpha, uRings);
  vec3 col = mix(BG, cover(vUv), reveal);
  col = mix(col, RING_LINE, clamp(lines, 0.0, 1.0) * 0.5 * uRings);
  float halo = exp(-max(outerDistance, 0.0) / (0.08 * uScale + 0.02));
  col += vec3(0.55, 0.16, 0.03) * halo * 0.22 * uRings * (1.0 - reveal);

  // light trail: gold over the dark, ember red over the revealed media; sandy and grainier where clicked
  vec2 trail = texture(uTrail, vUv).rg * uLight;
  vec2 grainCell = floor(gl_FragCoord.xy / uGrainSize);
  float glow = trailGlow(trail, grainCell, uTime);
  // drifting waves of light in the dark around the rings, pushed around by the pointer
  float ambient = texture(uAmbient, vUv).r * ambientGrain(grainCell, uTime) * uLight;
  vec3 overDark = col + GOLD * (glow * 0.75 + ambient * ${HERO_AMBIENT_STRENGTH.toFixed(3)});
  vec3 overMedia = mix(col, col * vec3(0.75, 0.16, 0.05) + EMBER * 0.25, clamp(glow * 2.2, 0.0, 0.9));
  col = mix(overDark, overMedia, reveal);

  // film grain: faint over the whole hero, stronger where the pointer clicked
  float grain = hash(grainCell * 1.37 + floor(uTime * 24.0) * 13.1) - 0.5;
  col += grain * (0.035 + 0.12 * trail.g);

  outColor = vec4(clamp(col, 0.0, 1.0), 1.0);
}`;

  const randomHarmonics = (spec) => spec.map(([h, min, max]) => ({ h, amp: rand(min, max), phase: rand(0, Math.PI * 2) }));

  const randomFlex = (spec) =>
    spec.map(([h, amp, speed, drift]) => ({
      h,
      amp,
      speed: speed * rand(0.9, 1.1),
      drift,
      phase: rand(0, Math.PI * 2),
      swellPhase: rand(0, Math.PI * 2),
    }));

  function flexSum(waves, theta, time) {
    let sum = 0;
    for (const { h, amp, phase, speed, drift, swellPhase } of waves)
      sum += amp * Math.sin(h * theta + phase + drift * time) * Math.sin(speed * time + swellPhase);
    return sum;
  }

  function harmonicSum(harmonics, theta) {
    let sum = 0;
    for (const { h, amp, phase } of harmonics) sum += amp * Math.cos(h * theta + phase);
    return sum;
  }

  class OudHeroEngine {
    /**
     * opts: canvas, image (poster), reducedMotion, onRevealChange(revealed, byTap), onTap(target),
     * onPointerInsideChange(inside)
     */
    constructor(opts) {
      this.opts = opts;
      const gl = opts.canvas.getContext('webgl2', {
        alpha: false,
        antialias: false,
        depth: false,
        stencil: false,
        premultipliedAlpha: false,
        powerPreference: 'low-power',
      });
      if (!gl) throw new Error('WebGL2 unavailable');
      this.gl = gl;

      this.shapeData = new Float32Array(SHAPE_SAMPLES * 4);
      this.imageAspect = 16 / 9;
      // the hero video, once set: its frames replace the poster image as soon as it has one
      this.video = null;
      this.videoTime = -1;
      this.videoSize = '';

      // layout
      this.width = 1;
      this.height = 1;
      this.pixelWidth = 1;
      this.pixelHeight = 1;
      this.grainSize = 1;
      this.ellipse = { x: 1, y: ELLIPSE_MINOR };
      this.startScale = 0.3;
      this.endScale = 2.6; // follows the ring's outline, see fitEndScale
      this.endScaleFitted = false;
      this.halfScale = 0.8; // inner ring scale that covers half the section
      this.halfProgress = 0.4; // reveal progress at which the inner ring covers half the section

      // reveal state
      this.progress = 0; // from taps
      this.tapTarget = 0; // where the taps so far want `progress` to be
      this.tapCount = 0;
      this.scrollTarget = 0; // from the pinned scroll, see setScrollProgress
      this.scrollProgress = 0;
      this.autoReveal = false;
      this.completed = false;
      this.ringsVisible = 1;
      this.pulse = 0;
      this.pulseVelocity = 0;
      this.shape = randomHarmonics(SHAPE_HARMONICS);
      this.shapeTarget = this.shape.map((h) => ({ ...h }));
      this.detail = randomHarmonics(DETAIL_HARMONICS);
      this.innerFlex = randomFlex(INNER_FLEX);
      this.midFlex = randomFlex(MID_FLEX);
      this.outerFlex = randomFlex(OUTER_FLEX);
      this.scale = 0.3;

      this.pointer = null; // uv, y up
      this.pointerInside = false;

      this.time = 0;
      this.lastFrame = 0;
      this.rafId = 0;
      this.active = false;
      this.destroyed = false;

      this.vao = gl.createVertexArray();
      this.trail = new LightTrail(gl, { size: HERO_LIGHT_SIZE });
      this.ambient = new AmbientLight(gl);
      this.compositeProgram = compileProgram(gl, HERO_SHADER);

      this.imageTexture = createTexture(gl);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 1, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE, new Uint8Array([20, 19, 19, 255]));
      this.uploadImage();

      this.shapeTexture = createTexture(gl, gl.REPEAT);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA16F, SHAPE_SAMPLES, 1, 0, gl.RGBA, gl.FLOAT, this.shapeData);
    }

    uploadImage() {
      const { image } = this.opts;
      if (!image.complete || !image.naturalWidth) {
        image.addEventListener('load', () => this.uploadImage(), { once: true });
        return;
      }
      // the video got there first
      if (this.destroyed || this.videoSize) return;
      const gl = this.gl;
      this.imageAspect = image.naturalWidth / image.naturalHeight;
      gl.bindTexture(gl.TEXTURE_2D, this.imageTexture);
      gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, image);
      gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
      this.requestFrame();
    }

    /** Shows this video's frames instead of the poster image (null: stays on the last frame). */
    setVideo(video) {
      this.video = video;
      this.videoTime = -1;
    }

    /** Copies the video's current frame into the media texture when it has moved on. */
    uploadVideoFrame() {
      const { video } = this;
      if (!video || video.readyState < video.HAVE_CURRENT_DATA || !video.videoWidth || video.currentTime === this.videoTime)
        return;
      this.videoTime = video.currentTime;
      this.videoSize = `${video.videoWidth}x${video.videoHeight}`;
      this.imageAspect = video.videoWidth / video.videoHeight;
      const gl = this.gl;
      gl.bindTexture(gl.TEXTURE_2D, this.imageTexture);
      gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, video);
      gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
    }

    resize(width, height, dpr) {
      if (width < 1 || height < 1) return;
      this.width = width;
      this.height = height;
      this.pixelWidth = Math.round(width * dpr);
      this.pixelHeight = Math.round(height * dpr);
      this.opts.canvas.width = this.pixelWidth;
      this.opts.canvas.height = this.pixelHeight;
      this.grainSize = Math.max(1, Math.round(dpr * 1.25)); // ~1.3 CSS px sand

      const aspect = width / height;
      const landscape = aspect >= 1;
      this.ellipse = landscape ? { x: 1, y: ELLIPSE_MINOR } : { x: ELLIPSE_MINOR, y: 1 };
      // Figma: the inner ring starts ~36% of the width wide on desktop and ~25% of the height tall on mobile
      this.startScale = landscape ? Math.min(0.3, 0.18 * aspect) : 0.14;
      // until the first frame fits it to the actual outline: big enough for any outline
      const cornerDistance = Math.hypot(aspect / 2, 0.5);
      const maxWobble = SHAPE_HARMONICS.reduce((sum, [, , max]) => sum + max, 0);
      this.endScale = (cornerDistance / (ELLIPSE_MINOR * (1 - maxWobble))) * 1.05;
      this.endScaleFitted = false;
      this.halfScale = Math.sqrt((0.5 * aspect) / (Math.PI * this.ellipse.x * this.ellipse.y));
      this.updateHalfProgress();

      this.trail.resize(this.pixelWidth, this.pixelHeight);
      this.ambient.resize(this.pixelWidth, this.pixelHeight);
      this.requestFrame();
    }

    /** Pointer position in CSS px relative to the canvas. */
    pointerMove(x, y) {
      this.pointer = { x: x / this.width, y: 1 - y / this.height };
      this.trail.pointerMove(this.pointer.x, this.pointer.y);
      this.ambient.pointerMove(this.pointer.x, this.pointer.y);
      this.updatePointerInside();
      this.requestFrame();
    }

    /** The page scrolled by `px` while the pointer rests over the (pinned) hero: glow in place. */
    scrollGlow(px) {
      if (!this.pointer || px <= 0) return;
      this.trail.glow(Math.min(0.2 + px / 100, 0.7));
      this.requestFrame();
    }

    pointerLeave() {
      this.pointer = null;
      this.trail.pointerLeave();
      this.ambient.pointerLeave();
      this.setPointerInside(false);
    }

    /** A click / tap: light burst where it landed and one step of the reveal. */
    pointerDown(x, y) {
      this.pointerMove(x, y);
      this.trail.pointerDown(this.pointer.x, this.pointer.y);
      this.ambient.pointerDown(this.pointer.x, this.pointer.y);
      this.tap();
    }

    /**
     * Scroll-driven reveal (0..1) from the pinned ScrollTrigger. Combines with taps: the rings follow
     * whichever is further along. Scrolling back closes them again.
     */
    setScrollProgress(progress) {
      const target = Math.min(Math.max(progress, 0), 1);
      if (target < this.scrollTarget) {
        const ratio = target / this.scrollTarget;
        // from fully open (a tapped reveal overshoots past 1), not from the overshoot
        this.progress = Math.min(this.progress, 1) * ratio;
        this.tapTarget = Math.min(this.tapTarget, 1) * ratio;
        this.tapCount = Math.floor(this.progressToTaps(this.tapTarget));
        this.autoReveal = false;
      }
      this.scrollTarget = target;
      this.requestFrame();
    }

    /** Reveal the media fully without further taps (end of the pin, scroll fallback, keyboard). */
    complete() {
      if (this.completed || this.autoReveal) return;
      this.autoReveal = true;
      this.progress = Math.max(this.progress, this.scrollProgress);
      this.requestFrame();
    }

    /** Back to where the scroll has the rings (scrolling back into the pin). */
    followScroll() {
      this.autoReveal = false;
      this.progress = Math.min(this.progress, 1);
      this.tapTarget = Math.min(this.tapTarget, this.scrollTarget);
      this.tapCount = Math.floor(this.progressToTaps(this.tapTarget));
      this.requestFrame();
    }

    setActive(active) {
      this.active = active;
      if (active) this.requestFrame();
      else if (this.rafId) {
        cancelAnimationFrame(this.rafId);
        this.rafId = 0;
      }
    }

    destroy() {
      this.destroyed = true;
      this.setActive(false);
      const gl = this.gl;
      this.trail.destroy();
      this.ambient.destroy();
      gl.deleteProgram(this.compositeProgram.program);
      gl.deleteTexture(this.imageTexture);
      gl.deleteTexture(this.shapeTexture);
      gl.deleteVertexArray(this.vao);
    }

    tap() {
      if (this.completed) return;
      // carry on from what's on screen if scrolling has already opened the rings further
      this.progress = Math.max(this.progress, this.scrollProgress);
      const opened = Math.floor(this.progressToTaps(this.progress) + 0.05);
      this.tapCount = Math.max(this.tapCount, opened) + 1;
      this.tapTarget = Math.max(this.tapTarget, this.tapsToProgress(this.tapCount));
      this.opts.onTap?.(Math.min(this.tapTarget, 1));
      if (!this.opts.reducedMotion) {
        this.pulseVelocity += PULSE_KICK;
        // each tap nudges the outline into a new shape
        this.shapeTarget = this.shapeTarget.map((harmonic, i) => {
          const [, min, max] = SHAPE_HARMONICS[i];
          return { h: harmonic.h, amp: rand(min, max), phase: harmonic.phase + rand(-0.8, 0.8) * (2 / harmonic.h) };
        });
      }
      this.requestFrame();
    }

    /** Growth is exponential in progress, so "half open" comes well before progress 0.5. */
    updateHalfProgress() {
      this.halfProgress = Math.min(
        Math.max(Math.log(this.halfScale / this.startScale) / Math.log(this.endScale / this.startScale), 0.1),
        0.9
      );
    }

    /** Scale at which the inner ring's current outline covers the whole section. */
    fitEndScale(dt) {
      const halfWidth = this.width / this.height / 2;
      let needed = 0;
      for (let i = 0; i < SHAPE_SAMPLES; i++) {
        const theta = (i / SHAPE_SAMPLES) * Math.PI * 2;
        const edge = Math.min(
          halfWidth / Math.max(Math.abs(Math.cos(theta)), 1e-6),
          0.5 / Math.max(Math.abs(Math.sin(theta)), 1e-6)
        );
        needed = Math.max(needed, edge / this.shapeData[i * 4]);
      }
      const target = Math.max(needed * FIT_MARGIN, this.startScale * 1.5);
      // eased while the outline morphs, so the opening doesn't jump with it
      this.endScale = this.endScaleFitted
        ? this.endScale + (target - this.endScale) * (1 - Math.exp(-dt / MORPH_TAU))
        : target;
      this.endScaleFitted = true;
      this.updateHalfProgress();
    }

    /** Tap count (fractional) -> reveal progress (0..1), and back. */
    tapsToProgress(taps) {
      const half = this.halfProgress;
      return taps <= HALF_TAPS
        ? (taps / HALF_TAPS) * half
        : half + ((Math.min(taps, REVEAL_TAPS) - HALF_TAPS) / (REVEAL_TAPS - HALF_TAPS)) * (1 - half);
    }

    progressToTaps(progress) {
      const half = this.halfProgress;
      return progress <= half
        ? (progress / half) * HALF_TAPS
        : HALF_TAPS + ((Math.min(progress, 1) - half) / (1 - half)) * (REVEAL_TAPS - HALF_TAPS);
    }

    /** Idle flex amplitudes for the current opening size (0 with reduced motion). */
    flexAmounts() {
      if (this.opts.reducedMotion) return { inner: 0, outer: 0 };
      const radiusPx = Math.max(1, this.height * this.scale);
      return {
        inner: Math.min(INNER_FLEX_AMOUNT, INNER_FLEX_MAX_PX / radiusPx),
        outer: Math.min(OUTER_FLEX_AMOUNT, OUTER_FLEX_MAX_PX / radiusPx),
      };
    }

    /** Inner ring radius (relative to scale). Shared by the shader and the pointer hit test. */
    radiusAt(theta, innerAmount) {
      const { x: ex, y: ey } = this.ellipse;
      const c = Math.cos(theta) / ex;
      const s = Math.sin(theta) / ey;
      const ellipse = 1 / Math.sqrt(c * c + s * s);
      return ellipse * (1 + harmonicSum(this.shape, theta) + innerAmount * flexSum(this.innerFlex, theta, this.time));
    }

    updatePointerInside() {
      if (!this.pointer) return;
      const aspect = this.width / this.height;
      const px = (this.pointer.x - 0.5) * aspect;
      const py = this.pointer.y - 0.5;
      const radius = this.radiusAt(Math.atan2(py, px), this.flexAmounts().inner);
      this.setPointerInside(this.completed || Math.hypot(px, py) < this.scale * radius);
    }

    setPointerInside(inside) {
      if (inside === this.pointerInside) return;
      this.pointerInside = inside;
      this.opts.onPointerInsideChange?.(inside);
    }

    requestFrame() {
      if (!this.active || this.rafId || this.destroyed) return;
      this.lastFrame = performance.now();
      this.rafId = requestAnimationFrame(this.frame);
    }

    frame = (now) => {
      this.rafId = 0;
      if (!this.active || this.destroyed) return;
      const dt = Math.min((now - this.lastFrame) / 1000, 0.05) || 1 / 60;
      this.lastFrame = now;
      this.time += dt;
      this.step(dt);
      this.render(dt);
      this.rafId = requestAnimationFrame(this.frame);
    };

    step(dt) {
      // reveal progress eases toward the tap target (which the forced reveal pushes along)
      if (this.autoReveal)
        this.tapTarget = Math.min(
          Math.max(this.tapTarget, this.progress, this.scrollProgress) + AUTO_REVEAL_SPEED * dt,
          OVERSHOOT
        );
      this.progress += (this.tapTarget - this.progress) * (1 - Math.exp(-dt / TAP_EASE_TAU));
      this.scrollProgress += (this.scrollTarget - this.scrollProgress) * (1 - Math.exp(-dt / SCROLL_EASE_TAU));
      const reveal = Math.max(this.progress, this.scrollProgress);
      if (!this.completed && reveal >= REVEALED_AT) {
        this.completed = true;
        const byTap = this.tapCount >= REVEAL_TAPS || (!this.autoReveal && this.progress >= this.scrollProgress);
        this.autoReveal = false;
        // tapped open: keep the rings expanding past the edges while they fade out
        if (this.progress >= this.scrollProgress) this.tapTarget = Math.max(this.tapTarget, OVERSHOOT);
        this.opts.onRevealChange?.(true, byTap);
        this.updatePointerInside();
      } else if (this.completed && !this.autoReveal && reveal < UNREVEALED_BELOW) {
        // scrolled back: the rings close over the media again
        this.completed = false;
        this.opts.onRevealChange?.(false, false);
        this.updatePointerInside();
      }
      const ringsTarget = this.completed ? 0 : 1;
      const ringsStep = dt / (this.completed ? RINGS_FADE_S : RINGS_RETURN_S);
      this.ringsVisible +=
        Math.sign(ringsTarget - this.ringsVisible) * Math.min(ringsStep, Math.abs(ringsTarget - this.ringsVisible));

      // tap pulse: a damped spring on the ring scale
      this.pulseVelocity += (-PULSE_STIFFNESS * this.pulse - PULSE_DAMPING * this.pulseVelocity) * dt;
      this.pulse += this.pulseVelocity * dt;

      // morph toward the latest tap's outline
      const k = 1 - Math.exp(-dt / MORPH_TAU);
      this.shape = this.shape.map((current, i) => {
        const target = this.shapeTarget[i];
        return {
          h: current.h,
          amp: current.amp + (target.amp - current.amp) * k,
          phase: current.phase + (target.phase - current.phase) * k,
        };
      });
      const growth = Math.pow(
        this.endScale / this.startScale,
        Math.min(Math.max(this.progress, this.scrollProgress), OVERSHOOT)
      );
      this.scale = this.startScale * growth * (1 + this.pulse * PULSE_SCALE);

      const flex = this.flexAmounts();
      const breathe = RING_BREATHE * Math.sin(this.time * RING_BREATHE_SPEED) * flex.outer;
      for (let i = 0; i < SHAPE_SAMPLES; i++) {
        const theta = (i / SHAPE_SAMPLES) * Math.PI * 2;
        const o = i * 4;
        this.shapeData[o] = this.radiusAt(theta, flex.inner);
        this.shapeData[o + 1] = harmonicSum(this.detail, theta);
        this.shapeData[o + 2] = flex.outer * flexSum(this.midFlex, theta, this.time) + breathe;
        this.shapeData[o + 3] = flex.outer * flexSum(this.outerFlex, theta, this.time) + breathe;
      }
      this.fitEndScale(dt);
      this.updatePointerInside();
    }

    render(dt) {
      const gl = this.gl;
      const { reducedMotion } = this.opts;
      const aspect = this.width / this.height;
      this.uploadVideoFrame();
      gl.bindVertexArray(this.vao);

      const trailTexture = this.trail.update(dt, this.time, aspect);
      const ambientTexture = this.ambient.update(dt, this.time, aspect);

      gl.viewport(0, 0, this.pixelWidth, this.pixelHeight);
      const comp = this.compositeProgram;
      gl.useProgram(comp.program);
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, this.imageTexture);
      gl.uniform1i(comp.u('uImage'), 0);
      gl.activeTexture(gl.TEXTURE1);
      gl.bindTexture(gl.TEXTURE_2D, this.shapeTexture);
      gl.texSubImage2D(gl.TEXTURE_2D, 0, 0, 0, SHAPE_SAMPLES, 1, gl.RGBA, gl.FLOAT, this.shapeData);
      gl.uniform1i(comp.u('uShape'), 1);
      gl.activeTexture(gl.TEXTURE2);
      gl.bindTexture(gl.TEXTURE_2D, trailTexture);
      gl.uniform1i(comp.u('uTrail'), 2);
      gl.activeTexture(gl.TEXTURE3);
      gl.bindTexture(gl.TEXTURE_2D, ambientTexture);
      gl.uniform1i(comp.u('uAmbient'), 3);
      gl.uniform2f(comp.u('uRes'), this.pixelWidth, this.pixelHeight);
      gl.uniform1f(comp.u('uImageAspect'), this.imageAspect);
      gl.uniform1f(comp.u('uScale'), this.scale);
      gl.uniform1f(comp.u('uRings'), this.ringsVisible);
      gl.uniform1f(comp.u('uTime'), reducedMotion ? 0 : this.time);
      gl.uniform1f(comp.u('uLight'), reducedMotion ? 0 : 1);
      gl.uniform1f(comp.u('uGrainSize'), this.grainSize);
      gl.uniform1fv(comp.u('uRingStep'), RING_STEP);
      gl.uniform1fv(comp.u('uRingAlpha'), RING_ALPHA);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
      gl.bindVertexArray(null);
    }
  }

  // ===============================================================================================
  // Light over the sections below the hero: one viewport-sized fixed canvas, screen-blended over the
  // page, that draws the pointer's light trail and the ambient waves. The trail is shifted with the
  // content every frame (measured on an anchor element, so pinned sections keep their light), only
  // drawn inside a vertical band (the Collection minus the hero), and its colour eases toward the
  // colour of the section at the centre of the viewport. A few elements (the CTAs) are lit from
  // above: their share of the light is copied onto a second canvas above the content.

  const COLOR_TAU = 0.45; // seconds to ease most of the way to a new section colour
  const AMBIENT_STRENGTH = 0.42; // the drifting waves: the background of the sections below the hero
  const AMBIENT_PARALLAX = 0.3; // the waves drift this much with the content while scrolling

  const LIGHT_SHADER = `#version 300 es
precision highp float;
in vec2 vUv;
out vec4 outColor;
uniform sampler2D uTrail;
uniform sampler2D uAmbient;
uniform vec3 uColor;
uniform vec2 uBand; // visible rows in device px from the bottom: [min, max)
uniform float uTime;
uniform float uGrainSize;
${HASH_GLSL}
${TRAIL_GLOW_GLSL}
${AMBIENT_GRAIN_GLSL}

void main() {
  float y = gl_FragCoord.y;
  // transparent outside the band: the canvas screen-blends only within the (isolated) Oud wrapper
  if (y < uBand.x || y >= uBand.y) {
    outColor = vec4(0.0);
    return;
  }
  vec2 trail = texture(uTrail, vUv).rg;
  vec2 grainCell = floor(gl_FragCoord.xy / uGrainSize);
  float glow = trailGlow(trail, grainCell, uTime);
  float ambient = texture(uAmbient, vUv).r * ambientGrain(grainCell, uTime) * ${AMBIENT_STRENGTH.toFixed(3)};
  outColor = vec4(clamp(uColor * (glow * 0.8 + ambient), 0.0, 1.0), 1.0);
}`;

  class OudLight {
    /**
     * opts: canvas, getTargetColor() -> [r, g, b], getBand() -> {top, bottom} (viewport CSS px),
     * getAnchor() -> Element
     */
    constructor(opts) {
      this.opts = opts;
      const gl = opts.canvas.getContext('webgl2', {
        alpha: true,
        antialias: false,
        depth: false,
        stencil: false,
        powerPreference: 'low-power',
      });
      if (!gl) throw new Error('WebGL2 unavailable');
      this.gl = gl;
      this.vao = gl.createVertexArray();
      this.trail = new LightTrail(gl, { size: LIGHT_SIZE });
      this.ambient = new AmbientLight(gl);
      this.ambientOffset = 0;
      this.composite = compileProgram(gl, LIGHT_SHADER);
      this.width = 1;
      this.height = 1;
      this.dpr = 1;
      this.color = [...opts.getTargetColor()];
      this.lastScrollY = window.scrollY;
      this.lastAnchor = null;
      this.lastAnchorTop = 0;
      this.time = 0;
      this.lastFrame = 0;
      this.rafId = 0;
      this.active = false;
      this.destroyed = false;
    }

    resize(width, height, dpr) {
      if (width < 1 || height < 1) return;
      this.width = width;
      this.height = height;
      this.dpr = Math.min(dpr, MAX_DPR);
      const pixelWidth = Math.round(width * this.dpr);
      const pixelHeight = Math.round(height * this.dpr);
      this.opts.canvas.width = pixelWidth;
      this.opts.canvas.height = pixelHeight;
      this.trail.resize(pixelWidth, pixelHeight);
      this.ambient.resize(pixelWidth, pixelHeight);
    }

    /** Pointer position in viewport CSS px. */
    pointerMove(clientX, clientY) {
      const x = clientX / this.width;
      const y = 1 - clientY / this.height;
      this.trail.pointerMove(x, y);
      this.ambient.pointerMove(x, y);
    }

    pointerDown(clientX, clientY) {
      const x = clientX / this.width;
      const y = 1 - clientY / this.height;
      this.trail.pointerDown(x, y);
      this.ambient.pointerDown(x, y);
    }

    pointerLeave() {
      this.trail.pointerLeave();
      this.ambient.pointerLeave();
    }

    setActive(active) {
      if (active === this.active || this.destroyed) return;
      this.active = active;
      if (active) {
        this.lastScrollY = window.scrollY;
        this.lastFrame = performance.now();
        this.rafId = requestAnimationFrame(this.frame);
      } else {
        if (this.rafId) cancelAnimationFrame(this.rafId);
        this.rafId = 0;
      }
    }

    destroy() {
      this.setActive(false);
      this.destroyed = true;
      this.trail.destroy();
      this.ambient.destroy();
      this.gl.deleteProgram(this.composite.program);
      this.gl.deleteVertexArray(this.vao);
    }

    frame = (now) => {
      this.rafId = 0;
      if (!this.active || this.destroyed) return;
      const dt = Math.min((now - this.lastFrame) / 1000, 0.05) || 1 / 60;
      this.lastFrame = now;
      this.time += dt;

      const target = this.opts.getTargetColor();
      const k = 1 - Math.exp(-dt / COLOR_TAU);
      for (let i = 0; i < 3; i++) this.color[i] += (target[i] - this.color[i]) * k;

      const scrollY = window.scrollY;
      const anchor = this.opts.getAnchor?.() ?? null;
      const anchorTop = anchor ? anchor.getBoundingClientRect().top : 0;
      const moved = anchor && anchor === this.lastAnchor ? this.lastAnchorTop - anchorTop : scrollY - this.lastScrollY;
      const shift = moved / this.height;
      // a pinned section stands still while the page scrolls: glow in place at the pointer instead
      const scrolled = Math.abs(scrollY - this.lastScrollY);
      if (scrolled > 0.5 && Math.abs(moved) < 0.5) this.trail.glow(Math.min(0.2 + scrolled / 100, 0.7));
      this.lastScrollY = scrollY;
      this.lastAnchor = anchor;
      this.lastAnchorTop = anchorTop;

      this.render(dt, shift);
      this.rafId = requestAnimationFrame(this.frame);
    };

    render(dt, shift) {
      const gl = this.gl;
      gl.bindVertexArray(this.vao);
      const aspect = this.width / this.height;
      const trailTexture = this.trail.update(dt, this.time, aspect, shift);
      this.ambientOffset -= shift * AMBIENT_PARALLAX;
      const ambientTexture = this.ambient.update(dt, this.time, aspect, shift, { x: 0, y: this.ambientOffset });

      const { program, u } = this.composite;
      const { top, bottom } = this.opts.getBand();
      gl.viewport(0, 0, this.opts.canvas.width, this.opts.canvas.height);
      gl.useProgram(program);
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, trailTexture);
      gl.uniform1i(u('uTrail'), 0);
      gl.activeTexture(gl.TEXTURE1);
      gl.bindTexture(gl.TEXTURE_2D, ambientTexture);
      gl.uniform1i(u('uAmbient'), 1);
      gl.uniform3f(u('uColor'), this.color[0], this.color[1], this.color[2]);
      gl.uniform2f(u('uBand'), (this.height - bottom) * this.dpr, (this.height - top) * this.dpr);
      gl.uniform1f(u('uTime'), this.time);
      gl.uniform1f(u('uGrainSize'), Math.max(1, Math.round(this.dpr * 1.25)));
      gl.drawArrays(gl.TRIANGLES, 0, 3);
      gl.bindVertexArray(null);
    }
  }

  /**
   * Feeds `light` the pointer over `target`; returns the cleanup. Touch moves come from touchmove,
   * which keeps firing while the page scrolls; a lifted finger's light lingers while the page is
   * still coasting; scrolling before the mouse has moved starts the light where the wheel happened.
   */
  function bindLightInput(target, light) {
    let pointerKnown = false;
    let leaveTimer = 0;
    const cancelLeave = () => {
      window.clearTimeout(leaveTimer);
      leaveTimer = 0;
    };
    const leave = () => {
      cancelLeave();
      pointerKnown = false;
      light.pointerLeave();
    };
    const linger = () => {
      window.clearTimeout(leaveTimer);
      leaveTimer = window.setTimeout(leave, 200);
    };
    const onPointerMove = (event) => {
      if (event.pointerType === 'touch') return;
      pointerKnown = true;
      light.pointerMove(event.clientX, event.clientY);
    };
    const onPointerDown = (event) => {
      cancelLeave();
      pointerKnown = true;
      light.pointerDown(event.clientX, event.clientY);
    };
    const onPointerLeave = (event) => {
      if (event.pointerType !== 'touch') leave();
    };
    const onTouchMove = (event) => {
      const touch = event.touches[0];
      if (!touch) return;
      cancelLeave();
      pointerKnown = true;
      light.pointerMove(touch.clientX, touch.clientY);
    };
    const onWheel = (event) => {
      if (pointerKnown) return;
      pointerKnown = true;
      light.pointerMove(event.clientX, event.clientY);
    };
    const onScroll = () => {
      if (leaveTimer) linger();
    };

    const passive = { passive: true };
    target.addEventListener('pointermove', onPointerMove, passive);
    target.addEventListener('pointerdown', onPointerDown, passive);
    target.addEventListener('pointerleave', onPointerLeave);
    target.addEventListener('touchmove', onTouchMove, passive);
    target.addEventListener('touchend', linger);
    target.addEventListener('touchcancel', leave);
    target.addEventListener('wheel', onWheel, passive);
    window.addEventListener('scroll', onScroll, passive);
    return () => {
      cancelLeave();
      target.removeEventListener('pointermove', onPointerMove);
      target.removeEventListener('pointerdown', onPointerDown);
      target.removeEventListener('pointerleave', onPointerLeave);
      target.removeEventListener('touchmove', onTouchMove);
      target.removeEventListener('touchend', linger);
      target.removeEventListener('touchcancel', leave);
      target.removeEventListener('wheel', onWheel);
      window.removeEventListener('scroll', onScroll);
    };
  }

  // ===============================================================================================
  // Scroll parallax inside `root`, one rAF-throttled scroll handler that only runs while `root` is
  // on screen: [data-parallax] images drift inside their clipping frame (their parent) at that
  // speed; [data-float] cards drift up/down, measured against their untransformed [data-float-cell].

  // Images are 124% of their frame, so they can travel 12% of the frame each way (~9.7% of their height).
  const MAX_SHIFT_PERCENT = 9.5;
  const CARD_DRIFT_PX = { desktop: 24, mobile: 16 };

  function setUpParallax(root) {
    const images = [...root.querySelectorAll('[data-parallax]')];
    const cards = [...root.querySelectorAll('[data-float]')];
    if (!images.length && !cards.length) return () => { };

    let rafId = 0;
    let visible = false;

    // -1 when the box enters at the bottom, 1 when it leaves at the top; null when hidden/off screen
    const progressOf = (box, viewport) => {
      if (!box.height || box.bottom < 0 || box.top > viewport) return null;
      return (viewport / 2 - (box.top + box.height / 2)) / (viewport / 2 + box.height / 2);
    };

    const update = () => {
      rafId = 0;
      const viewport = window.innerHeight;
      const drift = window.innerWidth >= 768 ? CARD_DRIFT_PX.desktop : CARD_DRIFT_PX.mobile;
      for (const card of cards) {
        const cell = card.closest('[data-float-cell]') ?? card.parentElement;
        const progress = progressOf(cell.getBoundingClientRect(), viewport);
        if (progress === null) continue;
        const shift = progress * drift * Number(card.dataset.float);
        card.style.transform = `translate3d(0, ${shift.toFixed(1)}px, 0)`;
      }
      for (const image of images) {
        const progress = progressOf(image.parentElement.getBoundingClientRect(), viewport);
        if (progress === null) continue;
        const shift = progress * MAX_SHIFT_PERCENT * Number(image.dataset.parallax);
        image.style.transform = `translate3d(0, ${shift.toFixed(2)}%, 0)`;
      }
    };
    const schedule = () => {
      if (visible && !rafId) rafId = requestAnimationFrame(update);
    };

    const observer = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting;
      schedule();
    });
    observer.observe(root);
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);

    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      if (rafId) cancelAnimationFrame(rafId);
      for (const el of [...images, ...cards]) el.style.transform = '';
    };
  }

  // ===============================================================================================
  // The two videos (Shopify Files renditions, see the snippet's JSON): the 16:9 cut from 768px up,
  // the 9:16 cut on phones, at the smallest rendition that stays sharp across the screen. One plays at
  // a time, looping, with the site's sound: the hero's from the hero through the whole Collection,
  // the "Oud lips" ending while it's in view (the hero's resumes once it's scrolled away again).

  const PLAY_RATIO = 0.2; // a video starts once this much of its section is on screen
  const LOAD_MARGIN = '50% 0px'; // gets ready when its section is within half a screen

  function pickVideo(video) {
    const wide = window.matchMedia('(min-width: 768px)').matches;
    const cut = wide ? video.wide : video.tall;
    const heights = Object.keys(cut.sources)
      .map(Number)
      .sort((a, b) => a - b);
    // full-bleed: device pixels across the screen, as a 16:9 (or 9:16) frame's height
    const devicePixels = window.innerWidth * Math.min(window.devicePixelRatio || 1, 2);
    const needed = wide ? (devicePixels * 9) / 16 : (devicePixels * 16) / 9;
    const height = heights.find((h) => h >= needed * 0.75) ?? heights[heights.length - 1];
    return { src: cut.sources[height], poster: cut.poster };
  }

  /** How much of an element (by height) is on screen, 0..1. */
  function shownFraction(rect, viewportHeight) {
    if (rect.height <= 0) return 0;
    const shown = Math.min(rect.bottom, viewportHeight) - Math.max(rect.top, 0);
    return Math.max(0, shown) / rect.height;
  }

  /** One looping video, played and paused by its owner; gets ready as `area` comes near. */
  function setUpVideo(element, area, video) {
    const { src, poster } = pickVideo(video);
    // CORS for the WebGL texture and the audio graph (the Shopify CDN allows any origin)
    element.crossOrigin = 'anonymous';
    element.muted = true;
    element.loop = true;
    element.playsInline = true;
    element.preload = 'none';
    element.poster = poster;

    let loaded = false;
    let wanted = false;
    const unregister = window.DossierSound?.registerSoundVideo(element, () => wanted) ?? (() => { });
    const load = () => {
      if (loaded) return;
      loaded = true;
      near.disconnect();
      element.preload = 'metadata';
      element.src = src;
    };
    const near = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        load();
        if (wanted) element.play().catch(() => undefined);
      },
      { rootMargin: LOAD_MARGIN }
    );
    near.observe(area);

    return {
      setWanted(next) {
        wanted = next;
        if (next) {
          load();
          element.play().catch(() => undefined);
        } else {
          element.pause();
        }
      },
      destroy() {
        near.disconnect();
        unregister();
        element.pause();
        element.removeAttribute('src');
        element.load();
      },
    };
  }

  function setUpVideos({ collection, hero, heroVideo, ending, endingVideo, videos }) {
    const heroPlayer = setUpVideo(heroVideo, hero, videos.hero);
    const endingPlayer = setUpVideo(endingVideo, ending, videos.lips);

    let inCollection = false;
    let endingInView = false;
    const update = () => {
      const height = window.innerHeight;
      const heroRect = hero.getBoundingClientRect();
      // past the Collection, or not at the hero yet: off; well into the hero (or below it): on
      if (collection.getBoundingClientRect().bottom <= 0 || heroRect.top >= height) inCollection = false;
      else if (heroRect.top <= height - heroRect.height * PLAY_RATIO) inCollection = true;
      const endingShown = shownFraction(ending.getBoundingClientRect(), height);
      if (endingShown >= PLAY_RATIO) endingInView = true;
      else if (endingShown <= 0) endingInView = false;
      // pause before playing the other, so the two never sound together
      if (endingInView) {
        heroPlayer.setWanted(false);
        endingPlayer.setWanted(true);
      } else {
        endingPlayer.setWanted(false);
        heroPlayer.setWanted(inCollection);
      }
    };
    const watch = (target, threshold) => {
      const observer = new IntersectionObserver(update, { threshold });
      observer.observe(target);
      return observer;
    };
    const observers = [watch(collection, [0]), watch(hero, [0, PLAY_RATIO]), watch(ending, [0, PLAY_RATIO])];

    return () => {
      observers.forEach((observer) => observer.disconnect());
      heroPlayer.destroy();
      endingPlayer.destroy();
    };
  }

  // ===============================================================================================
  // Hero: the media sits behind tree rings that morph and grow with every tap until it fills the
  // section, while the pointer leaves a trail of textured light. Scrolling through its pin opens it
  // too; past a third of it, it opens fully.

  const SCROLL_REVEAL_FRACTION = 0.3;

  class OudHero {
    constructor(section, { video, poster, reducedMotion, onReveal, onTap, onMore }) {
      this.section = section;
      this.revealed = false;
      this.engine = null;
      this.pointer = null; // where the pointer is on screen (and in the hero), to follow it while scrolling
      this.leaveTimer = 0;
      this.cursor = section.querySelector('.oud-hero__cursor');
      const canvas = section.querySelector('.oud-hero__canvas');
      const cleanups = [];
      this.cleanups = cleanups;

      section.querySelector('[data-oud-hero-reveal]')?.addEventListener('click', () => this.complete());
      section.querySelector('[data-oud-hero-more]')?.addEventListener('pointerdown', (event) => event.stopPropagation());
      section.querySelector('[data-oud-hero-more]')?.addEventListener('click', () => onMore?.());

      if (!canvas || !isWebGL2Supported()) {
        this.setUnsupported();
        return;
      }

      const image = new Image();
      image.decoding = 'async';
      // served from the Shopify CDN: without CORS the WebGL upload taints the canvas
      image.crossOrigin = 'anonymous';
      // the video's first frame until it plays (the still with reduced motion)
      image.src = poster;
      try {
        this.engine = new OudHeroEngine({
          canvas,
          image,
          reducedMotion,
          onRevealChange: (isRevealed, byTap) => {
            this.revealed = isRevealed;
            section.classList.toggle('is-revealed', isRevealed);
            if (isRevealed) onReveal?.(byTap);
          },
          onTap: (target) => onTap?.(target),
          onPointerInsideChange: (inside) => this.cursor?.classList.toggle('is-inside', inside),
        });
      } catch (error) {
        console.error('Oud hero: WebGL setup failed', error);
        this.setUnsupported();
        return;
      }
      const engine = this.engine;
      if (!reducedMotion) engine.setVideo(video);

      const resize = () => {
        const rect = section.getBoundingClientRect();
        engine.resize(rect.width, rect.height, Math.min(window.devicePixelRatio || 1, 1.5));
      };
      resize();
      const resizeObserver = new ResizeObserver(resize);
      resizeObserver.observe(section);
      // only animate while on screen
      const intersectionObserver = new IntersectionObserver(([entry]) => engine.setActive(entry.isIntersecting));
      intersectionObserver.observe(section);

      let lastScrollY = window.scrollY;
      const onScroll = () => {
        const rect = section.getBoundingClientRect();
        if (rect.top < -rect.height * SCROLL_REVEAL_FRACTION) engine.complete();

        // the light keeps shining while the page scrolls under a resting pointer: the hero moving
        // under it draws a trail; pinned (standing still), it glows in place
        const scrolled = Math.abs(window.scrollY - lastScrollY);
        lastScrollY = window.scrollY;
        const pointer = this.pointer;
        if (!pointer) return;
        const x = pointer.clientX - rect.left;
        const y = pointer.clientY - rect.top;
        if (x < 0 || y < 0 || x > rect.width || y > rect.height) {
          this.pointer = null;
          engine.pointerLeave();
          return;
        }
        if (Math.abs(x - pointer.x) > 0.5 || Math.abs(y - pointer.y) > 0.5) {
          pointer.x = x;
          pointer.y = y;
          engine.pointerMove(x, y);
          if (!pointer.touch) this.moveCursor(x, y);
        } else {
          engine.scrollGlow(scrolled);
        }
        // a lifted finger's light lingers while the page is still coasting
        if (pointer.touch && this.leaveTimer) this.lingerThenLeave();
      };
      window.addEventListener('scroll', onScroll, { passive: true });

      // Touch moves come from the touch events: the browser cancels touch pointers once it starts
      // scrolling, but keeps sending touchmove, so the light follows the finger while scrolling too.
      const onPointerMove = (event) => {
        if (event.pointerType === 'touch') return;
        const { x, y } = this.track(event.clientX, event.clientY, false);
        engine.pointerMove(x, y);
        if (event.pointerType === 'mouse') this.moveCursor(x, y);
      };
      const onPointerDown = (event) => {
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        const touch = event.pointerType === 'touch';
        const { x, y } = this.track(event.clientX, event.clientY, touch);
        engine.pointerDown(x, y);
        if (event.pointerType === 'mouse') this.moveCursor(x, y);
      };
      const onPointerLeave = (event) => {
        if (event.pointerType !== 'touch') this.leave();
      };
      const onTouchMove = (event) => {
        const touch = event.touches[0];
        if (!touch) return;
        const { x, y } = this.track(touch.clientX, touch.clientY, true);
        engine.pointerMove(x, y);
      };
      const onTouchEnd = () => this.lingerThenLeave();
      const onTouchCancel = () => this.leave();
      // scrolling before the mouse has moved: start the light where the wheel happened
      const onWheel = (event) => {
        if (this.pointer) return;
        const { x, y } = this.track(event.clientX, event.clientY, false);
        engine.pointerMove(x, y);
        this.moveCursor(x, y);
      };
      const listeners = [
        ['pointermove', onPointerMove],
        ['pointerdown', onPointerDown],
        ['pointerleave', onPointerLeave],
        ['touchmove', onTouchMove],
        ['touchend', onTouchEnd],
        ['touchcancel', onTouchCancel],
        ['wheel', onWheel],
      ];
      for (const [type, listener] of listeners) section.addEventListener(type, listener, { passive: true });

      cleanups.push(() => {
        window.removeEventListener('scroll', onScroll);
        for (const [type, listener] of listeners) section.removeEventListener(type, listener);
        window.clearTimeout(this.leaveTimer);
        resizeObserver.disconnect();
        intersectionObserver.disconnect();
        engine.destroy();
        this.engine = null;
      });
    }

    setUnsupported() {
      this.section.classList.add('is-unsupported');
    }

    moveCursor(x, y) {
      if (!this.cursor) return;
      this.cursor.style.left = `${x}px`;
      this.cursor.style.top = `${y}px`;
    }

    /** Pointer at a viewport position: remembered for scrolling, local position returned. */
    track(clientX, clientY, touch) {
      window.clearTimeout(this.leaveTimer);
      this.leaveTimer = 0;
      const rect = this.section.getBoundingClientRect();
      const x = clientX - rect.left;
      const y = clientY - rect.top;
      this.pointer = { clientX, clientY, x, y, touch };
      return { x, y };
    }

    leave() {
      window.clearTimeout(this.leaveTimer);
      this.leaveTimer = 0;
      this.pointer = null;
      this.engine?.pointerLeave();
    }

    // lifted finger: keep its light while the page keeps scrolling (momentum), then let go
    lingerThenLeave() {
      window.clearTimeout(this.leaveTimer);
      this.leaveTimer = window.setTimeout(() => this.leave(), 200);
    }

    setScrollProgress(progress) {
      this.engine?.setScrollProgress(progress);
    }

    complete() {
      if (this.engine) this.engine.complete();
    }

    followScroll() {
      this.engine?.followScroll();
    }

    isRevealed() {
      return this.revealed;
    }

    destroy() {
      this.cleanups.forEach((cleanup) => cleanup());
    }
  }

  // ===============================================================================================
  // Scroll choreography, from quiet to loud:
  // 1. Hero: pinned under the header while scrolling uncovers the media (taps still work), then once
  //    fully open it lets go, and scales back and dims as it hands over to the collection.
  // 2. Bundle: the photo settles from a slight zoom, the heading rises word by word, then the CTA.
  // 3. Scents: the product image wipes open from below while the photo settles, the name rises word
  //    by word, the notes and the CTA slide up, and the background photo slowly un-zooms.
  // 4. Moodboards: on desktop rows wipe open in place; on mobile the cards rise in a random stagger.
  // 5. Finale: the lips settle from a slight zoom as they scroll into view.
  // Every tap on the hero also moves the page through its pin to where scrolling would have opened
  // it that far; once the last tap opens it, the page glides on to the bundle a little later.

  // Scrolling opens the rings this far by the end of the pin; they then open fully by themselves.
  const HERO_SCROLL_REVEAL = 0.85;
  const BUNDLE_SCROLL_DELAY_MS = 2500; // after the last tap: let the open hero sink in, then glide on
  const BUNDLE_SCROLL_DURATION_S = 1.8;
  const TAP_SCROLL_DURATION_S = 0.6;
  const ASSEMBLE_SCRUB_S = 0.8; // seconds the scroll-scrubbed assemblies take to catch up
  const NOTE_DRAG_S = 1.8; // the notes' slide-up trails the scroll more, so it keeps an easy pace
  const NOTE_EASE = 'power1.out';
  const TAP_SCROLL_SHARE = 0.92; // of the pin, for taps up to where scrolling alone opens it
  // scroll input hands control back to the visitor (taps come with a little touchmove, so only the
  // glide to the bundle listens to it)
  const TAP_SCROLL_INTERRUPTS = ['wheel', 'keydown'];
  const GLIDE_INTERRUPTS = ['wheel', 'touchmove', 'keydown'];

  /**
   * Splits `element`'s text into words, each in a clipping mask (what GSAP's SplitText does with
   * `mask: 'words'`), keeping inline elements such as <strong>. Returns the moving inner spans. Split
   * once: the choreography runs again on every breakpoint change.
   */
  function splitWords(element) {
    if (element.dataset.oudSplit) return [...element.querySelectorAll('.oud-word__inner')];
    element.dataset.oudSplit = 'words';
    const inners = [];
    const split = (node) => {
      for (const child of [...node.childNodes]) {
        if (child.nodeType === Node.ELEMENT_NODE) {
          split(child);
          continue;
        }
        if (child.nodeType !== Node.TEXT_NODE || !child.textContent.trim()) continue;
        const fragment = document.createDocumentFragment();
        for (const part of child.textContent.split(/(\s+)/)) {
          if (!part) continue;
          if (/^\s+$/.test(part)) {
            fragment.append(' ');
            continue;
          }
          const mask = document.createElement('span');
          mask.className = 'oud-word';
          const inner = document.createElement('span');
          inner.className = 'oud-word__inner';
          inner.textContent = part;
          mask.append(inner);
          fragment.append(mask);
          inners.push(inner);
        }
        child.replaceWith(fragment);
      }
    };
    split(element);
    return inners;
  }

  function setUpChoreography(root, hero) {
    const api = {
      releaseHeroPin: null,
      syncHeroScroll: null,
      revealedByTap: null,
      glideToBundle: null,
    };
    const { gsap, ScrollTrigger } = window;
    if (!gsap || !ScrollTrigger) return { api, destroy: () => { } };
    gsap.registerPlugin(ScrollTrigger);
    const q = (scope, selector) => gsap.utils.toArray(scope.querySelectorAll(selector));
    const mm = gsap.matchMedia();

    mm.add(
      {
        desktop: '(min-width: 768px) and (prefers-reduced-motion: no-preference)',
        mobile: '(max-width: 767px) and (prefers-reduced-motion: no-preference)',
      },
      (context) => {
        const { desktop, mobile } = context.conditions;
        if (!desktop && !mobile) return;

        // pinned sections sit just below the sticky site header; the hero is sized to fit there
        let headerPx = 0;
        const syncHeader = () => {
          headerPx = headerBottom();
          root.style.setProperty('--oud-header', `${headerPx}px`);
        };
        syncHeader();
        ScrollTrigger.addEventListener('refreshInit', syncHeader);
        // parts of the header can settle after we start: re-check once it has
        const headerChecks = [600, 1500, 3500].map((ms) =>
          window.setTimeout(() => {
            if (Math.abs(headerBottom() - headerPx) > 2) ScrollTrigger.refresh();
          }, ms)
        );

        // one scripted scroll at a time (tap sync, glide to the bundle)
        let stopGlide;
        const glideTo = (to, { duration, ease, delay = 0, interrupts, onDone }) => {
          stopGlide?.();
          let timer = 0;
          let tween;
          const stop = () => {
            if (stopGlide === stop) stopGlide = undefined;
            window.clearTimeout(timer);
            tween?.kill();
            for (const type of interrupts) window.removeEventListener(type, stop);
          };
          stopGlide = stop;
          for (const type of interrupts) window.addEventListener(type, stop, { passive: true });
          const run = () => {
            const target = to();
            const done = () => {
              stop();
              onDone?.();
            };
            if (Math.abs(target - window.scrollY) < 2) {
              done();
              return;
            }
            const position = { y: window.scrollY };
            let last = position.y;
            tween = gsap.to(position, {
              y: target,
              duration,
              ease,
              onUpdate: () => {
                // something else moved the page (e.g. the scrollbar): let it
                if (Math.abs(window.scrollY - last) > 3) {
                  stop();
                  return;
                }
                window.scrollTo({ top: position.y, behavior: 'instant' });
                last = window.scrollY;
              },
              onComplete: done,
            });
          };
          if (delay) timer = window.setTimeout(run, delay);
          else run();
        };

        // 1. hero: scroll uncovers it while pinned, then it recedes
        const heroWrapper = root.querySelector('[data-oud-hero]');
        const heroSection = heroWrapper?.querySelector('.oud-hero');
        if (heroWrapper && heroSection) {
          let pin = null;
          if (!hero.isRevealed()) {
            pin = ScrollTrigger.create({
              trigger: heroWrapper,
              pin: true,
              anticipatePin: 1,
              // centred in the space below the header (the hero is at most that tall)
              start: () => {
                const header = headerBottom();
                const free = window.innerHeight - header - heroWrapper.offsetHeight;
                return `top ${header + Math.max(0, free / 2)}px`;
              },
              end: () => `+=${Math.round(window.innerHeight * (desktop ? 1.4 : 1.2))}`,
              invalidateOnRefresh: true,
              // eased because ring growth is exponential and would otherwise open mostly up front
              onUpdate: (self) => hero.setScrollProgress(self.progress ** 1.5 * HERO_SCROLL_REVEAL),
              onLeave: () => hero.complete(),
              onEnterBack: () => hero.followScroll(),
            });
          }
          // taps move the page along, never ahead of the rings
          api.syncHeroScroll = (reveal) => {
            if (!pin) return;
            const upToScroll = Math.min(reveal / HERO_SCROLL_REVEAL, 1);
            const pastScroll = Math.max((reveal - HERO_SCROLL_REVEAL) / (1 - HERO_SCROLL_REVEAL), 0);
            const progress =
              reveal >= 1 ? 1 : upToScroll ** (2 / 3) * TAP_SCROLL_SHARE + pastScroll * (0.995 - TAP_SCROLL_SHARE);
            const target = () =>
              pin ? Math.min(pin.start + progress * (pin.end - pin.start), pin.end - 1) : window.scrollY;
            if (target() <= window.scrollY + 1) return; // never scroll back
            glideTo(target, { duration: TAP_SCROLL_DURATION_S, ease: 'power2.out', interrupts: TAP_SCROLL_INTERRUPTS });
          };
          api.releaseHeroPin = () => {
            if (pin && window.scrollY < pin.start) {
              pin.kill(true);
              pin = null;
              ScrollTrigger.refresh();
            }
          };

          gsap.to(heroSection, {
            scale: desktop ? 0.9 : 0.94,
            opacity: 0.35,
            borderRadius: desktop ? 32 : 20,
            transformOrigin: '50% 100%',
            ease: 'none',
            scrollTrigger: {
              trigger: heroWrapper,
              start: () => (pin ? pin.end : 'bottom bottom'),
              end: () => (pin ? pin.end + heroWrapper.offsetHeight : 'bottom top'),
              scrub: true,
            },
          });
        }

        // 2. bundle: its two layers zoom each on their own, not as a group (a transformed wrapper
        // would put the light above or below both)
        const bundle = root.querySelector('[data-oud-bundle]');
        if (bundle) {
          gsap.fromTo(
            bundle.querySelectorAll('[data-oud-bundle-image]'),
            { scale: 1.12 },
            {
              scale: 1,
              ease: 'none',
              // settled once all of it is on screen: the bottles and the CTA sit uncropped
              scrollTrigger: { trigger: bundle, start: 'top bottom', end: 'bottom bottom', scrub: true },
            }
          );
          const heading = bundle.querySelector('[data-oud-bundle-heading]');
          const reveal = gsap.timeline({
            defaults: { ease: 'power3.out' },
            scrollTrigger: {
              trigger: bundle,
              start: 'top 85%',
              // done just before where the glide from the hero stops (centred under the header)
              end: desktop ? 'center 58%' : 'bottom 95%',
              scrub: ASSEMBLE_SCRUB_S,
            },
          });
          if (heading) reveal.from(splitWords(heading), { yPercent: 110, stagger: 0.08, duration: 0.6 });
          const savings = bundle.querySelector('[data-oud-bundle-savings]');
          const tooltip = bundle.querySelector('.info-tooltip');
          if (savings) reveal.from(savings, { y: 24, opacity: 0, duration: 0.6 }, '-=0.3');
          if (tooltip) reveal.from(tooltip, { y: 24, opacity: 0, duration: 0.6 }, '-=0.3');
          reveal.from(bundle.querySelector('[data-oud-bundle-cta]'), { y: 30, opacity: 0, duration: 0.6 }, '+=0.2');
        }

        // the bundle centred in the space under the header, or just under it if it's taller
        const bundleTarget = () => {
          if (!bundle) return window.scrollY;
          const rect = bundle.getBoundingClientRect();
          const free = window.innerHeight - headerPx - rect.height;
          return rect.top + window.scrollY - headerPx - Math.max(0, free / 2);
        };
        api.glideToBundle = () =>
          glideTo(bundleTarget, {
            duration: BUNDLE_SCROLL_DURATION_S * 0.75,
            ease: 'power2.inOut',
            interrupts: GLIDE_INTERRUPTS,
          });
        // last tap: finish the page's move to the end of the hero pin, then later glide on
        api.revealedByTap = () => {
          const pin = heroWrapper && ScrollTrigger.getAll().find((t) => t.pin === heroWrapper);
          const later = () => {
            if (bundleTarget() > window.scrollY + 8)
              glideTo(bundleTarget, {
                delay: BUNDLE_SCROLL_DELAY_MS,
                duration: BUNDLE_SCROLL_DURATION_S,
                ease: 'power2.inOut',
                interrupts: GLIDE_INTERRUPTS,
              });
          };
          if (pin && window.scrollY < pin.end - 2)
            glideTo(() => pin.end - 1, {
              duration: TAP_SCROLL_DURATION_S,
              ease: 'power2.out',
              interrupts: TAP_SCROLL_INTERRUPTS,
              onDone: later,
            });
          else later();
        };

        // 3. scent reveals
        for (const scent of q(root, '[data-oud-scent]')) {
          const block = scent.querySelector('[data-oud-scent-block]');
          const product = scent.querySelector('[data-oud-product]');
          const title = scent.querySelector('[data-oud-title]');
          const scentScroll = {
            trigger: block,
            start: 'top 85%',
            // desktop: done once centred; mobile (stacked, taller): once its end is on screen
            end: desktop ? 'center 55%' : 'bottom 95%',
          };
          const reveal = gsap
            .timeline({ defaults: { ease: 'power3.out' }, scrollTrigger: { ...scentScroll, scrub: ASSEMBLE_SCRUB_S } })
            .from(product, { clipPath: 'inset(100% 0% 0% 0%)', duration: 1.2, ease: 'power3.inOut' })
            .from(product.querySelector('img'), { scale: 1.25, duration: 1.6 }, '<')
            .from(splitWords(title), { yPercent: 110, stagger: 0.07, duration: 0.8 }, '<0.35')
            .from(scent.querySelector('[data-oud-desc]'), { y: 24, opacity: 0, duration: 0.8 }, '<0.15');
          // the notes (then the CTA and its explore link) on a timeline of their own with more drag
          const notesStart = reveal.duration() - 0.5;
          const notes = gsap
            .timeline({ defaults: { ease: NOTE_EASE }, scrollTrigger: { ...scentScroll, scrub: NOTE_DRAG_S } })
            .from(q(scent, '[data-oud-note]'), { y: 24, opacity: 0, stagger: 0.2, duration: 1 }, notesStart)
            .from(
              q(scent, '[data-oud-cta], [data-oud-explore]'),
              { y: 24, opacity: 0, stagger: 0.15, duration: 0.6 },
              notesStart + 0.9
            );
          // pad the main timeline to the same length so both follow the same scroll positions
          reveal.set({}, {}, notes.duration());

          const background = scent.querySelector('[data-oud-scent-bg]');
          if (background)
            gsap.fromTo(
              background,
              { scale: 1.18 },
              {
                scale: 1,
                ease: 'none',
                scrollTrigger: { trigger: block, start: 'top bottom', end: 'bottom 40%', scrub: true },
              }
            );

          // 4. moodboard comes in (only the layout for this breakpoint)
          const moodboard = scent.querySelector(
            desktop ? '[data-oud-moodboard="desktop"]' : '[data-oud-moodboard="mobile"]'
          );
          const cards = moodboard ? q(moodboard, '[data-oud-card]') : [];
          if (desktop) {
            // the grid stays put: each row wipes open in place, left to right, as it comes in
            for (let row = 0; row < cards.length; row += 4) {
              const rowCards = cards.slice(row, row + 4);
              gsap.from(rowCards, {
                clipPath: 'inset(100% 0% 0% 0%)',
                duration: 1.1,
                ease: 'power3.inOut',
                stagger: 0.1,
                scrollTrigger: { trigger: rowCards[0], start: 'top 90%', once: true },
              });
            }
          } else if (cards.length) {
            gsap.from(cards, {
              y: 70,
              scale: 0.9,
              opacity: 0,
              duration: 1.2,
              ease: 'power3.out',
              stagger: { each: 0.09, from: 'random' },
              scrollTrigger: { trigger: moodboard, start: 'top 85%', once: true },
            });
          }
        }

        // 5. finale: the lips settle from a slight zoom
        const ending = root.querySelector('[data-oud-ending]');
        const lips = ending?.querySelector('video');
        if (ending && lips)
          gsap.fromTo(
            lips,
            { scale: 1.15 },
            {
              scale: 1,
              ease: 'none',
              scrollTrigger: { trigger: ending, start: 'top bottom', end: 'bottom bottom', scrub: 0.6 },
            }
          );

        return () => {
          headerChecks.forEach((id) => window.clearTimeout(id));
          ScrollTrigger.removeEventListener('refreshInit', syncHeader);
          api.releaseHeroPin = null;
          api.syncHeroScroll = null;
          api.revealedByTap = null;
          api.glideToBundle = null;
          stopGlide?.();
        };
      }
    );

    // the page's images load in later: re-measure the pins and triggers once they have
    const refresh = () => ScrollTrigger.refresh();
    if (document.readyState !== 'complete') window.addEventListener('load', refresh, { once: true });

    return {
      api,
      destroy: () => {
        window.removeEventListener('load', refresh);
        mm.revert();
      },
    };
  }

  // ===============================================================================================

  class OudCollection extends HTMLElement {
    connectedCallback() {
      if (this.cleanups) return;
      this.cleanups = [];
      const reduced = reducedMotion();
      const videos = JSON.parse(this.querySelector('[data-oud-videos]')?.textContent || 'null');
      const heroWrapper = this.querySelector('[data-oud-hero]');
      const heroVideo = this.querySelector('.oud-hero__video');
      const ending = this.querySelector('[data-oud-ending]');
      const endingVideo = ending?.querySelector('video');
      const bundleWrapper = this.querySelector('.oud-bundle');

      bundleWrapper.addEventListener("click", this.clickBundle)

      // reduced motion: the stills instead of the videos (CSS swaps them), nothing moves
      if (!reduced && videos && heroWrapper && heroVideo && ending && endingVideo)
        this.cleanups.push(
          setUpVideos({ collection: this, hero: heroWrapper, heroVideo, ending, endingVideo, videos })
        );
      if (!reduced) this.cleanups.push(setUpParallax(this));

      let choreography = { api: {} };
      const scrollToCollection = () => {
        if (choreography.api.glideToBundle) {
          choreography.api.glideToBundle();
          return;
        }
        // no choreography (reduced motion): a plain smooth scroll
        const bundle = this.querySelector('[data-oud-bundle]');
        if (!bundle) return;
        window.scrollTo({
          top: bundle.getBoundingClientRect().top + window.scrollY - headerBottom(),
          behavior: 'smooth',
        });
      };
      const heroSection = heroWrapper?.querySelector('.oud-hero');
      if (heroSection) {
        const hero = new OudHero(heroSection, {
          video: heroVideo,
          poster: reduced || !videos ? this.dataset.heroPoster : pickVideo(videos.hero).poster,
          reducedMotion: reduced,
          // e.g. the keyboard reveal: nobody has to scroll through an image that is already open
          onReveal: (byTap) => (byTap ? choreography.api.revealedByTap?.() : choreography.api.releaseHeroPin?.()),
          onTap: (reveal) => choreography.api.syncHeroScroll?.(reveal),
          onMore: scrollToCollection,
        });
        this.cleanups.push(() => hero.destroy());
        choreography = setUpChoreography(this, hero);
        this.cleanups.push(() => choreography.destroy());
      }

      this.setUpLight(reduced);

      // already on this perfume's page: its top is the place to explore it
      for (const link of this.querySelectorAll('[data-oud-explore][data-oud-top]')) {
        link.addEventListener('click', (event) => {
          event.preventDefault();
          window.scrollTo({ top: 0, behavior: 'smooth' });
        });
      }
    }

    clickBundle(e) {
      if (!e.target.closest('.oud-bundle__cta') && !e.target.closest('.info-tooltip')) {
        console.log('bundle btn')
        window.location.href = `${Shopify.routes.root}products/oud-collection-bundle`
      }
    }

    disconnectedCallback() {
      this.cleanups?.forEach((cleanup) => cleanup());
      this.cleanups = null;
    }

    /** The light cursor over everything below the hero (the hero draws its own). */
    setUpLight(reduced) {
      const canvas = this.querySelector('.oud-light');
      const heroWrapper = this.querySelector('[data-oud-hero]');
      if (!canvas || reduced || !isWebGL2Supported()) return;

      // the section at the centre of the viewport sets the light colour and anchors its movement
      const centerSection = () => {
        const center = window.innerHeight / 2;
        for (const section of this.querySelectorAll('[data-oud-light]')) {
          const rect = section.getBoundingClientRect();
          if (rect.top <= center && rect.bottom > center) return section;
        }
        return null;
      };
      const gold = this.dataset.lightGold.split(',').map(Number);
      let light;
      try {
        light = new OudLight({
          canvas,
          getTargetColor: () => centerSection()?.dataset.oudLight?.split(',').map(Number) ?? gold,
          getAnchor: centerSection,
          getBand: () => ({
            top: Math.max(heroWrapper?.getBoundingClientRect().bottom ?? 0, 0),
            bottom: Math.min(this.getBoundingClientRect().bottom, window.innerHeight),
          }),
        });
      } catch (error) {
        console.error('Oud light: WebGL setup failed', error);
        return;
      }

      const viewport = document.documentElement;
      const resize = () => light.resize(viewport.clientWidth, viewport.clientHeight, window.devicePixelRatio || 1);
      resize();
      window.addEventListener('resize', resize);
      const unbind = bindLightInput(this, light);
      const observer = new IntersectionObserver(([entry]) => {
        // hide the fixed canvas with the section so no stale light lingers over the rest of the page
        canvas.style.visibility = entry.isIntersecting ? 'visible' : 'hidden';
        light.setActive(entry.isIntersecting);
      });
      observer.observe(this);

      this.cleanups.push(() => {
        observer.disconnect();
        window.removeEventListener('resize', resize);
        unbind();
        light.destroy();
      });
    }
  }

  customElements.define('oud-collection', OudCollection);
  /**
   * "Bundle & save": the three Oud perfumes to the cart in one go, then the cart drawer, like the
   * theme's product forms.
   */
  class OudBundleAdd extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('button');
      this.button?.addEventListener('click', () => this.add());
    }

    add() {
      const button = this.button;
      if (!button || button.getAttribute('aria-disabled') === 'true') return;
      // const variantIds = "67121895375219";
      const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      button.setAttribute('aria-disabled', 'true');
      button.classList.add('loading');
      button.querySelector(".loading-overlay__spinner").classList.remove('hidden');
      const body = { items: [{ id: '67121895375219', quantity: 1 }] };
      if (cart) {
        body.sections = cart.getSectionsToRender().map((section) => section.id);
        body.sections_url = window.location.pathname;
        cart.setActiveElement(document.activeElement);
      }
      if (document.body.classList.contains('OfferHDN')) body.attributes = { dsrDiscounts: 'true' };

      fetch(`${window.routes.cart_add_url}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      })
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            if (typeof publish === 'function')
              publish(PUB_SUB_EVENTS.cartError, { source: 'oud-bundle', errors: response.description });
            console.error('Oud bundle: add to cart failed', response.description);
            return;
          }
          if (!cart) {
            window.location = window.routes.cart_url;
            return;
          }
          if (typeof publish === 'function')
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'oud-bundle', cartData: response });
          cart.renderContents(response, false, this.dataset.products.split(','));
          if (cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
        })
        .catch((error) => console.error(error))
        .finally(() => {
          button.classList.remove('loading');
          button.removeAttribute('aria-disabled');
          button.querySelector(".loading-overlay__spinner").classList.add('hidden');
          if (typeof cartLimitReached === 'function') setTimeout(() => cartLimitReached(), 1500);
          if (typeof updateAnnouncementBar === 'function') updateAnnouncementBar();
        });
    }
  }

  customElements.define('oud-bundle-add', OudBundleAdd);
})();

window.addEventListener("DOMContentLoaded", (e) => {
  let soundToggle = document.querySelector('.oud-collection-sound-toggle')
  let video = document.querySelector('.oud-hero__video')
  if (!soundToggle) return;

  soundToggle.addEventListener('click', (e) => {
    // console.log('toggle volume')
    soundToggle.classList.toggle('sound-on')
    if (soundToggle.classList.contains('sound-on')) {

      // mute all other videos
      let videos = document.querySelectorAll('video');
      videos.forEach(vid => {
        vid.muted = true;
        let parent = vid.closest('.collection-showcase_video');
        if (parent) {
          let muteIcon = parent.querySelector('.mute-icon');
          muteIcon.src = 'https://cdn.shopify.com/s/files/1/0557/9908/7202/files/mute-icon-m.svg?v=1759481296';
        }
      })
      //

      video.muted = false
    }
    else {
      video.muted = true
    }
  })
})
